import type { FC } from "react"
import React, { useMemo } from "react"

import { useTranslation } from "react-i18next"
import { IoClose } from "react-icons/io5"
import { selector, useRecoilState, useRecoilValue, atom } from "recoil"
import styled from "styled-components"

import IS_DEV from "~/utils/isDev"
import IS_QA from "~/utils/isQa"

import { PoisonDesignMessages } from "./PoisonDesignMessages"
import { queryErrorNotificationState } from "./queryErrorNotifications"
import {
  poisonedDesignState,
  poisonedEstimatesRemoteState,
  poisonedSavingsState,
} from "../designs/design-state"
import { tpoSellerLicenseErrorState } from "../loan-products/tpo-loan-product"
import { poisonedQuoteRemoteState } from "../quotes/quotes-state"
import { poisonedSiteImageryState, siteRemoteState } from "../site/site-state"

type NotificationKeys =
  | `poisonedDesign`
  | `poisonedEstimate`
  | `poisonedImagery`
  | `poisonedQuote`
  | `poisonedSavings`
  | `queryErrors`
  | `tpoLicenseError`

export type Notification = {
  key: NotificationKeys
  shouldShow: boolean
}

// https://mdigi.tools/color-shades
export const NOTIFICATION_COLORS = {
  warning: {
    textColor: `#805500`,
    backgroundColor: `#ffc95edf`,
    border: `#b37700`,
  },
  danger: {
    textColor: `#800000`,
    backgroundColor: `#ff9191e1`,
    border: `#b30000`,
  },
}
export type NotificationColors = keyof typeof NOTIFICATION_COLORS

export const NotificationMessage = styled.div<{
  textColor?: string
  border?: string
  backgroundColor?: string
}>`
  position: relative;
  border-radius: 5px;
  padding: 5px 15px;
  width: 100%;
  max-height: 200px;
  overflow-y: auto;
  border: 1px solid ${({ border }) => border ?? `#8a6c314c`};
  background-color: ${({ backgroundColor }) => backgroundColor ?? `#ffc95edf`};
  box-shadow: 0px 4px 8px rgba(0, 0, 0, 0.138);
  [data-role="divider"] {
    margin: 5px 0;
    padding: 0;
    background-color: ${({ border }) => border ?? `#8a6c314c`};
    height: 1px;
  }
  span,
  a,
  p {
    color: ${({ textColor }) => textColor ?? `white`};
    font-weight: 500;
    font-size: var(--step--1);
  }
  a,
  [data-role="link"] {
    display: flex;
    align-items: center;
    gap: 5px;
    cursor: pointer;
    font-size: var(--step--1);
    width: fit-content;
    margin: 3px 0;
    background-color: #ffffffcf;
    border: 1px solid var(--blue);
    border-radius: 5px;
    padding: 4px 7px;
    color: #0091ff;
    svg {
      fill: #0091ff;
      font-size: var(--step--2);
    }
  }
  > p {
    font-weight: 500;
  }
`
const DismissibleNotification = styled.div<{
  textColor?: string
}>`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  > main {
    width: 100%;
  }
  svg {
    cursor: pointer;
    fill: ${({ textColor }) => textColor ?? `white`};
  }
`

export const useNotificationMessages = (): { key: string; message: FC }[] => {
  const site = useRecoilValue(siteRemoteState)
  const notifications = useRecoilValue(notificationsState)

  const poisonedEstimate = useRecoilValue(poisonedEstimatesRemoteState)
  const poisonedImagery = useRecoilValue(poisonedSiteImageryState)
  const poisonedQuote = useRecoilValue(poisonedQuoteRemoteState)
  const [queryErrors, setQueryErrors] = useRecoilState(
    queryErrorNotificationState,
  )

  const { t } = useTranslation()

  const items = useMemo(() => {
    const messages = {
      poisonedDesign: {
        key: `poisonedDesign`,
        message: () => <PoisonDesignMessages />,
      },
      poisonedQuote: {
        key: `poisonedQuote`,
        message: () => (
          <NotificationMessage {...NOTIFICATION_COLORS.danger}>
            <p>{t(`poisonedQuote`)}</p>
            {poisonedQuote && (
              <>
                <div data-role="divider" />
                <p>{poisonedQuote.reason}</p>
              </>
            )}
          </NotificationMessage>
        ),
      },
      poisonedEstimate: {
        key: `poisonedEstimate`,
        message: () => (
          <NotificationMessage {...NOTIFICATION_COLORS.danger}>
            <p>{t(`poisonedEstimate`)}</p>
            <div data-role="divider" />
            <p># of Failed Estimates: {poisonedEstimate?.length}</p>
          </NotificationMessage>
        ),
      },
      poisonedImagery: {
        key: `poisonedImagery`,
        message: () => (
          <NotificationMessage {...NOTIFICATION_COLORS.danger}>
            {poisonedImagery?.map((pi, index) => (
              <React.Fragment key={pi.id}>
                <p>{pi.reason}</p>
                {index !== poisonedImagery.length - 1 && (
                  <div data-role="divider" />
                )}
              </React.Fragment>
            ))}
          </NotificationMessage>
        ),
      },
      poisonedSavings: {
        key: `poisonedSavings`,
        message: () => (
          <NotificationMessage {...NOTIFICATION_COLORS.danger}>
            <p>{t(`poisonedSavings`)}</p>
          </NotificationMessage>
        ),
      },
      tpoLicenseError: {
        key: `tpoLicenseError`,
        message: () => (
          <NotificationMessage
            {...NOTIFICATION_COLORS.danger}
            onClick={(e) => {
              e.stopPropagation()
            }}
          >
            <p>
              A sales license is required to request TPO documents in{` `}
              {site.state}. Please enter your license information in platform and
              retry your request.{` `}
            </p>
            <div data-role="divider" />
            <a
              href={
                IS_DEV || IS_QA
                  ? `https://www.powur.dev/profile/service-areas`
                  : `https://www.powur.com/profile/service-areas`
              }
              target="_blank"
              rel="noreferrer"
            >
              Update here
            </a>
          </NotificationMessage>
        ),
      },
      queryErrors: {
        key: `queryErrors`,
        message: () => (
          <NotificationMessage
            {...NOTIFICATION_COLORS.danger}
            onClick={(e) => e.stopPropagation()}
          >
            <DismissibleNotification {...NOTIFICATION_COLORS.danger}>
              <main>
                {queryErrors.map((error, index) => (
                  <div key={error.code}>
                    <p>{error.message}</p>
                    {error.howToFix && <p>{error.howToFix}</p>}
                    {index !== queryErrors.length - 1 && (
                      <div data-role="divider" />
                    )}
                  </div>
                ))}
              </main>
              <IoClose onClick={() => setQueryErrors(() => [])} />
            </DismissibleNotification>
          </NotificationMessage>
        ),
      },
    }
    const temp = []
    for (const notification of notifications) {
      if (messages[notification.key]) {
        temp.push(messages[notification.key])
      }
    }

    return temp
  }, [
    notifications,
    poisonedEstimate,
    poisonedImagery,
    t,
    poisonedQuote,
    site.state,
    queryErrors,
    setQueryErrors,
  ])

  return items
}

export const notificationsState = selector({
  key: `notificationsState`,
  get: ({ get }) => {
    const poisonedQuote = get(poisonedQuoteRemoteState)
    const poisonedDesign = get(poisonedDesignState)
    const poisonedEstimate = get(poisonedEstimatesRemoteState)
    const poisonedImagery = get(poisonedSiteImageryState)
    const poisonedSavings = get(poisonedSavingsState)
    const tpoLicenseError = get(tpoSellerLicenseErrorState)
    const queryErrors = get(queryErrorNotificationState)
    const notifications: Notification[] = []
    const possible: Notification[] = [
      {
        key: `poisonedQuote`,
        shouldShow: !!poisonedQuote,
      },
      {
        key: `poisonedSavings`,
        shouldShow: !!poisonedSavings,
      },
      {
        key: `poisonedDesign`,
        shouldShow: !!poisonedDesign,
      },
      {
        key: `poisonedEstimate`,
        shouldShow: poisonedEstimate && poisonedEstimate.length > 0,
      },
      {
        key: `poisonedImagery`,
        shouldShow: poisonedImagery && poisonedImagery.length > 0,
      },
      {
        key: `tpoLicenseError`,
        shouldShow: tpoLicenseError,
      },
      {
        key: `queryErrors`,
        shouldShow: queryErrors.length > 0,
      },
    ]
    possible.forEach((item) => {
      if (item.shouldShow) {
        notifications.push(item)
      }
    })

    return notifications
  },
})
